import React, { useState, useEffect, useRef } from "react";
import Table from "@/common/elements/Table";
import {
  InmateDetailsTableContainer,
  TableHeader,
} from "../../InmateDetails/styles";
import Button from "@/common/elements/Button";
import { Popconfirm } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import { v4 as uuidv4 } from "uuid";
import {
  getFullAddress,
  hasCheckSecurity,
  isNonEmptyVariable,
} from "@/util/common_function";
import useDialog from "@/hooks/useDialog";
import CustomModal from "../../CustomModal";
import AddressesForm from "./AddressesForm";
import API_ENDPOINTS from "@/util/api";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";

export const AddressesList = ({
  selectedAddresses,
  associates,
  onAddressesChange,
  hideActions = false,
  fullWidth = false,
}) => {
  const { showDialog } = useDialog();
  const dispatch = useDispatch();
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state.bookings
  );
  const addressFormRef = useRef();

  const [showEdit, setShowEdit] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectAddress, setSelectAddress] = useState(null);
  const [addressData, setAddressData] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [isDiasbled, setIsDisabled] = useState(false);
  // Use the selectedAddresses prop if available; otherwise, use Redux value.
  useEffect(() => {
    let addresses;
    if (associates) {
      // When associates is true, use selectedAddresses (even if null, default to empty array)
      addresses = selectedAddresses || [];
    } else {
      addresses = bookings?.bio?.[0]?.bioAddresses || [];
    }
    // Process addresses only if they exist
    addresses = addresses.map((a) => ({
      ...a,
      type: a?.addresses?.[0]?.addressType || "",
      fullAddress: getFullAddress(a?.addresses?.[0]),
      uuid: a.uuid || uuidv4(),
    }));
    setAddressData(addresses);
  }, [selectedAddresses, bookings, associates]);

  const openModal = () => {
    setShowModal(true);
  };

  const handleEditAddress = () => {
    setIsEdit(true);
    openModal();
  };

  const handleAddAddress = () => {
    setSelectAddress(null);
    setIsEdit(false);
    openModal();
  };

  const selectAddressDataFn = (value) => {
    setSelectAddress({
      ...value?.addresses?.[0],
      uuid: value?.uuid,
    });
    setShowEdit(true);
  };

  const handleSave = () => {
    addressFormRef?.current?.validateFields().then((values) => {
      let ad = [...addressData];
      if (isEdit) {
        ad = ad.map((a) => {
          if (a?.uuid === selectAddress.uuid) {
            return {
              ...a,
              addresses: [
                {
                  ...((a.addresses && a.addresses[0]) || {}),
                  ...values,
                },
              ],
            };
          }
          return a;
        });
      } else {
        const data = { addresses: [values], uuid: uuidv4() };
        ad.push(data);
      }

      if (associates) {
        // In associates mode, update parent through onAddressesChange (done in useEffect)
        setAddressData(ad);
        if (typeof onAddressesChange === "function") {
          onAddressesChange(ad);
        }
      } else {
        dispatch(
          setBookings({
            bio: [
              {
                ...bookings?.bio?.[0],
                bioAddresses: ad,
              },
            ],
          })
        );
      }
      handleCancel();
    });
  };

  const preDelete = () => {
    showDialog("confirm", {
      title: "Are you sure you want to delete this address?",
      onConfirm: () => handleDelete(),
    });
  };

  const handleDelete = () => {
    const selAddressData = addressData.find(
      (p) => p?.uuid === selectAddress?.uuid
    );
    const updatedAddressData = addressData.filter(
      (p) => p?.uuid !== selectAddress?.uuid
    );
    setAddressData(updatedAddressData);

    if (!associates) {
      dispatch(
        setBookings({
          bio: [
            {
              ...bookings?.bio?.[0],
              bioAddresses: updatedAddressData,
            },
          ],
        })
      );
    }

    if (isNonEmptyVariable(selAddressData?.bioAddressId)) {
      const url =
        API_ENDPOINTS.deleteBioAddresses + "/" + selAddressData?.bioAddressId;
      dispatch(setDeleteAPis(url));
    }

    if (isNonEmptyVariable(selAddressData?.addressId)) {
      const url = API_ENDPOINTS.addresses + "/id/" + selAddressData?.addressId;
      dispatch(setDeleteAPis(url));
    }

    handleCancel();
  };

  const handleCancel = () => {
    setShowModal(false);
    setShowEdit(false);
    setSelectAddress(null);
    setIsEdit(false);
  };
  useEffect(() => {
    if (showEdit) {
      let showSave = hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.MODIFY,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Arrest_Report
      );
      if (showSave) {
        setIsDisabled(false);
      } else {
        setIsDisabled(true);
      }
    } else {
      setIsDisabled(false);
    }
  }, [showEdit]);
  return (
    <>
      <InmateDetailsTableContainer
        style={{ width: fullWidth ? "100%" : "auto" }}
      >
        <TableHeader>
          <h6>List of address</h6>
          {!hideActions && (
            <div className="right">
              {
                <Button
                  onClick={handleAddAddress}
                  disabled={
                    !bookingsFieldsEnabled ||
                    !hasCheckSecurity(
                      Security_Types.functionality,
                      Entity_types.subModule,
                      LABELS_CAPITAL.ADD,
                      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                      SUB_MODULES_LIST_WITH_ID.Arrest_Report
                    )
                  }
                >
                  Add Address
                </Button>
              }
              {
                <Button
                  onClick={handleEditAddress}
                  style={{ marginLeft: "10px" }}
                  disabled={
                    !showEdit ||
                    !hasCheckSecurity(
                      Security_Types.functionality,
                      Entity_types.subModule,
                      LABELS_CAPITAL.VIEW,
                      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                      SUB_MODULES_LIST_WITH_ID.Arrest_Report
                    )
                  }
                >
                  Details
                </Button>
              }
              {
                <Button
                  style={{ marginLeft: "10px" }}
                  disabled={
                    !showEdit ||
                    !bookingsFieldsEnabled ||
                    !hasCheckSecurity(
                      Security_Types.functionality,
                      Entity_types.subModule,
                      LABELS_CAPITAL.DELETE,
                      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                      SUB_MODULES_LIST_WITH_ID.Arrest_Report
                    )
                  }
                  onClick={preDelete}
                >
                  Delete
                </Button>
              }
              <Button
                onClick={handleCancel}
                style={{ marginLeft: "10px" }}
                disabled={!bookingsFieldsEnabled || !showEdit}
              >
                Cancel
              </Button>
            </div> )
          }
        </TableHeader>

        {hasCheckSecurity(
          Security_Types.functionality,
          Entity_types.subModule,
          LABELS_CAPITAL.VIEW,
          MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
          SUB_MODULES_LIST_WITH_ID.Arrest_Report
        ) && (
          <Table
            headers={[
              { key: "type", text: "Type" },
              { key: "fullAddress", text: "Full Address" },
            ]}
            data={addressData}
            onRowClick={selectAddressDataFn}
            onRowDoubleClick={(value) => {
              console.log("OPEN MODAL FROM DOUBLE CLICK");
              selectAddressDataFn(value);
              handleEditAddress();
            }}
            selected={selectAddress === null}
          />
        )}
      </InmateDetailsTableContainer>

      <CustomModal
        header="Address Information"
        designPattern="ant"
        show={showModal}
        onHide={handleCancel}
        onSaveClick={handleSave}
        onCloseClick={handleCancel}
        submitButtonDisabled={!bookingsFieldsEnabled}
        isDisabled={!isDiasbled && !bookingsFieldsEnabled}
        width={"50%"}
        size="lg"
      >
        {showModal && (
          <AddressesForm 
            formData={selectAddress || {}} 
            ref={addressFormRef} 
            bookingsFieldsEnabled={bookingsFieldsEnabled}
          />
        )}
      </CustomModal>
    </>
  );
};

export default AddressesList;
